import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from '../uiSlice';
import "./game.css";
import { fetchReport } from "../../functions/fetchReport";
import { sendReport } from "../../functions/sendReport";
import {
  isMultiplaySession,
  readMultiplayRoundDone,
  writeMultiplayRoundDone,
} from "../../utils/multiplaySession";

// import gameBg from "../../img/gamebg.png";
// import gameBgMob from "../../img/gamebgmob.png";
import driftHole from "../../img/drift.gif";
import driftOpen from "../../img/driftopen.gif";
import hammerStill from "../../img/1.png";
import hammerHit2 from "../../img/2.png";
import hammerHit3 from "../../img/3.png";
import hammerHit4 from "../../img/4.png";
import hammerHit5 from "../../img/5.png";
import hammerHit6 from "../../img/6.png";
import hammerHit7 from "../../img/7.png";
import hammerHit8 from "../../img/8.png";
import hammerHit9 from "../../img/9.png";

import localImpactSound from "../../assets/sound/impact.mp3";
/** Full HTTPS URL (e.g. `https://bucket.s3.region.amazonaws.com/.../impact.mp3`). Overrides key + theme below. */
const ENV_IMPACT_SOUND_URL = String(process.env.REACT_APP_IMPACT_SOUND_URL || "").trim();
/** Filename or path under `REACT_APP_S3_PATH` — e.g. `impact.mp3` */
const ENV_IMPACT_SOUND_KEY = String(
  process.env.REACT_APP_IMPACT_SOUND_KEY ?? process.env.REACT_APP_IMPACT_S3_FILENAME ?? ""
).trim();

/** When `REACT_APP_S3_PATH` is set but theme/env omit the clip, assume this object exists in that prefix. */
const DEFAULT_S3_IMPACT_FILENAME = "impact.mp3";

function normalizeImpactThemeValue(v) {
  if (v == null) return "";
  if (Array.isArray(v)) {
    const first = v.find((x) => x != null && String(x).trim());
    return first != null ? String(first).trim() : "";
  }
  if (typeof v === "string") return v.trim();
  const s = String(v).trim();
  return /^[\s\[]*[{\[]/.test(s) ? "" : s;
}

/** Idle hammer = `1.png`; strike sequence = `2.png` … `8.png`. */
const HAMMER_HIT_FRAMES = [
  hammerHit2,
  hammerHit3,
  hammerHit4,
  hammerHit5,
  hammerHit6,
  hammerHit7,
  hammerHit8,
  hammerHit9,

];
const HAMMER_FRAME_MS = 34;

const HOLE_COUNT = 6;
const INITIAL_ACTIVE_HOLES = 3;
const SECOND_PHASE_ACTIVE_HOLES = 4;
const FINAL_PHASE_ACTIVE_HOLES = 5;
const SECOND_PHASE_START_SECONDS = 30;
const FINAL_PHASE_START_SECONDS = 60;
const ROUND_SECONDS = 30;
/** Minimum center-to-center distance in % so drift art does not stack (see .game-hole size). */
const MIN_DISTANCE = 28;
/** How often holes get new positions (CSS transition should stay a bit shorter than this). */
const HOLE_MOVE_INTERVAL_MS = 4200;
/** Stronger ramp each minute so movement clearly speeds up over a session. */
const MOVE_SPEEDUP_PER_MIN_MS = 900;
const MIN_MOVE_INTERVAL_MS = 1400;
const MIN_MOVE_TRANSITION_MS = 700;
/** How often a new wave of moles pops up. */
const POP_INTERVAL_MS = 4000;
const POP_SPEEDUP_PER_MIN_MS = 0;
const MIN_POP_INTERVAL_MS = 4000;
/** Sequential delay between mole spawns in one wave — avoids popping together at once (ms). */
const MIN_SPAWN_STAGGER_MS = 2000;
const MAX_SPAWN_STAGGER_MS = 3000;
const TIMER_STORAGE_KEY = "echo_smash_timer_state_v1";
const SCORE_STORAGE_KEY = "echo_smash_score_state_v1";
const REPORT_SENT_STORAGE_KEY = "echo_smash_report_sent_v1";
const MAX_TIMER_RESUME_AGE_MS = 30000;
const DEFAULT_HIT_SCORE_GOOD = -5;
const DEFAULT_HIT_SCORE_BAD = 10;
const TARGET_TOTAL_HITS = 50;
/** Align hammer art with pointer (pairs with `.game-hammer` translate in CSS). */
const HAMMER_CURSOR_OFFSET = { x: 44, y: 44 };
/** Mobile hit effect: place hammer a bit left so head lands on tap target. */
const MOBILE_HAMMER_HIT_OFFSET = { x: 0, y: 0 };
const MOBILE_CONTROL_ZONE_START = 0.55;
/** Fine-tuned hammer strike point so collision matches visible head/tip. */
const HAMMER_HIT_POINT_OFFSET = { x: 26, y: -24 };
const HAMMER_HIT_CENTER_OFFSET = { x: 20, y: 2 };
/** Bottom of vertical hit zone as fraction of hole height from top (higher = larger click area). */
const HOLE_TOP_HIT_RATIO = 0.94;
/** Half-width of accepted hit strip as fraction of hole width from center (~0.5 ≈ nearly full width). */
const HOLE_HIT_MAX_DX_RATIO = 0.48;

const LOCAL_GOOD_SPRITES = [];
const LOCAL_BAD_SPRITES = [];

const randomInt = (max) => Math.floor(Math.random() * max);
const randomBetween = (min, max) => min + Math.random() * (max - min);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const getDefaultHammerPosition = (mobile) => {
  if (!mobile) {
    // Keep original desktop default.
    return {
      x: window.innerWidth - 140,
      y: window.innerHeight - 160,
    };
  }

  // Mobile: place hammer in screen right-bottom corner.
  const targetX = window.innerWidth - 86;
  const targetY = window.innerHeight - 104;

  return {
    x: clamp(targetX, 10, Math.max(10, window.innerWidth - 70)),
    y: clamp(targetY, 70, Math.max(70, window.innerHeight - 85)),
  };
};

/** Pre-spaced anchors (%); pairwise spacing ≥ MIN_DISTANCE for fallback placement. */
const ANCHOR_SLOTS = [
  { x: 12, y: 28 },
  { x: 50, y: 30 },
  { x: 88, y: 28 },
  { x: 14, y: 56 },
  { x: 50, y: 58 },
  { x: 86, y: 56 },
];

const shuffleAnchors = () => {
  const copy = [...ANCHOR_SLOTS];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const parseArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
};

/** Encode each path segment; decode first so already-encoded keys are not double-encoded. */
const encodeThemePathSegments = (pathPart) =>
  String(pathPart)
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");

/**
 * Normalize http(s) URLs whose path contains spaces/specials (invalid for `new URL` until encoded).
 */
const normalizeHttpThemeAssetUrl = (raw) => {
  const trimmed = String(raw).trim();
  const m = trimmed.match(/^(https?:\/\/[^/?#]+)(\/[^?#]*)?(\?[^#]*)?(#.*)?$/i);
  if (!m) return trimmed;
  const origin = m[1];
  const pathSlash = m[2] || "";
  const search = m[3] || "";
  const hash = m[4] || "";
  const pathPart = pathSlash.replace(/^\//, "");
  if (!pathPart) return `${origin}${search}${hash}`;
  const encoded = encodeThemePathSegments(pathPart);
  return `${origin}/${encoded}${search}${hash}`;
};

const buildThemeAssetUrl = (asset) => {
  if (asset == null) return "";
  const raw = String(asset).trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const pathOnly = u.pathname.replace(/^\//, "");
      if (!pathOnly) return `${u.origin}${u.search}${u.hash}`;
      const encoded = encodeThemePathSegments(pathOnly);
      return `${u.origin}/${encoded}${u.search}${u.hash}`;
    } catch {
      return normalizeHttpThemeAssetUrl(raw);
    }
  }

  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/+$/, "");
  if (!base) return raw;
  const encoded = encodeThemePathSegments(raw);
  return `${base}/${encoded}`;
};

/**
 * Hit sound URL: env full URL → env key relative to `REACT_APP_S3_PATH` → theme `impact_sound`
 * → default `impact.mp3` under S3 prefix when configured.
 */
function resolveImpactSoundUrl(theme) {
  if (ENV_IMPACT_SOUND_URL) {
    // Use env URL verbatim when absolute so query strings / pre-encoded paths (incl. presigned URLs) stay intact.
    if (/^https?:\/\//i.test(ENV_IMPACT_SOUND_URL)) {
      return ENV_IMPACT_SOUND_URL;
    }
    const fromEnv = buildThemeAssetUrl(ENV_IMPACT_SOUND_URL);
    if (fromEnv) return fromEnv;
  }
  if (ENV_IMPACT_SOUND_KEY) {
    const fromKey = buildThemeAssetUrl(ENV_IMPACT_SOUND_KEY);
    if (fromKey) return fromKey;
  }
  const raw = normalizeImpactThemeValue(theme?.impact_sound);
  if (raw) {
    const resolved = buildThemeAssetUrl(raw);
    if (resolved) return resolved;
  }
  const s3Configured = Boolean(String(process.env.REACT_APP_S3_PATH || "").trim());
  if (s3Configured) {
    const fallback = buildThemeAssetUrl(DEFAULT_S3_IMPACT_FILENAME);
    if (fallback) return fallback;
  }
  return localImpactSound;
}

/** Safe for `element.style.backgroundImage` (quoted `url()` handles odd characters). */
const cssBackgroundImageUrl = (url) => {
  if (!url) return "none";
  const escaped = String(url).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
};

/** Filename for local `public/theme-assets/<name>` lookup. */
const themeSpriteBasename = (asset) => {
  const raw = String(asset ?? "").trim();
  if (!raw) return "";
  const path = raw.split("?")[0].split("#")[0];
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      const seg = u.pathname.split("/").filter(Boolean);
      return seg[seg.length - 1] || "";
    } catch {
      return "";
    }
  }
  const segments = path.replace(/^\/+/, "").split("/");
  return segments[segments.length - 1] || path;
};

/** Optional fallback: CRA `public/img/<basename>` when S3 is unreachable. */
const buildLocalThemeSpriteUrl = (asset) => {
  const name = themeSpriteBasename(asset);
  if (!name) return "";
  const publicBase = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${publicBase}/img/${encodeURIComponent(name)}`;
};

/** Prefer S3 (`REACT_APP_S3_PATH` + theme key), then local public copy. */
const pickReachableImageUrl = async (s3Url, localUrl) => {
  if (s3Url && (await checkImageExists(s3Url))) return s3Url;
  if (localUrl && (await checkImageExists(localUrl))) return localUrl;
  return s3Url || localUrl || "";
};

const resolveThemeSpriteUrl = async (sprite) => {
  const raw = String(sprite ?? "").trim();
  if (!raw) return "";
  const primaryUrl = buildThemeAssetUrl(raw);
  const localUrl = /^https?:\/\//i.test(raw) ? "" : buildLocalThemeSpriteUrl(raw);
  return pickReachableImageUrl(primaryUrl, localUrl);
};

const checkImageExists = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

const buildHole = (id) => ({
  id,
  x: 0,
  y: 0,
  popType: "none",
  spriteIndex: 0,
  hitPulse: false,
  isSqueezing: false,
  goingDown: false,
  visibleUntil: 0,
});

const MIN_VISIBLE_MS = 800;
const MAX_VISIBLE_MS = 2000;
const MIN_ACTIVE_MOLES = 1;
/** Before final hole phase (< 5 holes): up to this many moles per wave. */
const MAX_ACTIVE_MOLES = 2;
/** When all 5 holes are active: random count between these (clamped to idle holes). */
const FINAL_PHASE_MIN_MOLES = 2;
const FINAL_PHASE_MAX_MOLES = 3;
const MIN_OBJECT_GAP_MS = 2000;
const LOCAL_DRIFT_X_RANGE = 12;
const LOCAL_DRIFT_Y_RANGE = 9;
const DESKTOP_HOLE_BOUNDS = {
  xMin: 10,
  xMax: 90,
  yMin: 24,
  yMax: 85,
  driftX: LOCAL_DRIFT_X_RANGE,
  driftY: LOCAL_DRIFT_Y_RANGE,
};
const MOBILE_HOLE_BOUNDS = {
  xMin: 12,
  xMax: 88,
  yMin: 22,
  yMax: 72,
  driftX: 8,
  driftY: 6,
};
const GOING_DOWN_DURATION_MS = 220;
const POP_WAVE_MIN_FACTOR = 0.75;
const POP_WAVE_MAX_FACTOR = 1.35;
const POP_RETRY_WHILE_MOVING_MS = 180;
const HIT_HIDE_DELAY_MS = 460;
const HIT_POINTS_FADE_MS = 720;
const HIT_RESPAWN_COOLDOWN_MS = 12000;
const getHoleBounds = (mobile) => (mobile ? MOBILE_HOLE_BOUNDS : DESKTOP_HOLE_BOUNDS);

const checkOverlap = (hole1, hole2, minDistance = MIN_DISTANCE) => {
  const dx = Math.abs(hole1.x - hole2.x);
  const dy = Math.abs(hole1.y - hole2.y);
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance < minDistance;
};

const generateNonOverlappingPosition = (
  existingHoles,
  minDistance = MIN_DISTANCE,
  baseHole = null,
  bounds = DESKTOP_HOLE_BOUNDS
) => {
  const maxAttempts = 380;
  const { xMin, xMax, yMin, yMax, driftX, driftY } = bounds;

  const hasBasePosition =
    baseHole &&
    Number.isFinite(baseHole.x) &&
    Number.isFinite(baseHole.y) &&
    (baseHole.x !== 0 || baseHole.y !== 0);

  if (hasBasePosition) {
    for (let attempts = 0; attempts < maxAttempts; attempts += 1) {
      const newHole = {
        x: clamp(
          baseHole.x + randomBetween(-driftX, driftX),
          xMin,
          xMax
        ),
        y: clamp(
          baseHole.y + randomBetween(-driftY, driftY),
          yMin,
          yMax
        ),
      };
      const hasOverlap = existingHoles.some((hole) => checkOverlap(newHole, hole, minDistance));
      if (!hasOverlap) return newHole;
    }
  }

  for (let attempts = 0; attempts < maxAttempts; attempts += 1) {
    const newHole = {
      x: randomBetween(xMin, xMax),
      y: randomBetween(yMin, yMax),
    };
    const hasOverlap = existingHoles.some((hole) => checkOverlap(newHole, hole, minDistance));
    if (!hasOverlap) return newHole;
  }

  for (const anchor of shuffleAnchors()) {
    const candidate = { x: anchor.x, y: anchor.y };
    if (!existingHoles.some((hole) => checkOverlap(candidate, hole, minDistance))) {
      return candidate;
    }
  }

  let best = { x: (xMin + xMax) / 2, y: (yMin + yMax) / 2 };
  let bestMinSep = -1;
  for (const anchor of ANCHOR_SLOTS) {
    const minSep =
      existingHoles.length === 0
        ? Infinity
        : Math.min(
            ...existingHoles.map((h) => {
              const dx = anchor.x - h.x;
              const dy = anchor.y - h.y;
              return Math.sqrt(dx * dx + dy * dy);
            })
          );
    if (minSep > bestMinSep) {
      bestMinSep = minSep;
      best = { ...anchor };
    }
  }
  return best;
};

const makeRandomPositions = (holes, bounds = DESKTOP_HOLE_BOUNDS) => {
  const indices = holes.map((_, i) => i);
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  const placed = [];
  indices.forEach((holeIdx) => {
    const hole = holes[holeIdx];
    const newPosition = generateNonOverlappingPosition(
      placed,
      MIN_DISTANCE,
      hole,
      bounds
    );
    placed.push({
      ...hole,
      ...newPosition,
      hitPulse: false,
      isSqueezing: false,
    });
  });
  placed.sort((a, b) => a.id - b.id);
  return placed;
};

/** Theme `use_timeout` from admin: `"false"` → elapsed timer, no auto end; otherwise countdown. */
const themeUsesCountdown = (td) => {
  if (!td) return true;
  const v = td.use_timeout;
  return !(v === false || v === "false");
};

const getTimerSettings = (td) => {
  if (!td) return { totalSeconds: ROUND_SECONDS, useCountdown: true };

  const timerValue = td.timer || "05:00";
  const [minutes, seconds] = timerValue.split(":").map(Number);
  const totalSeconds = (minutes || 0) * 60 + (seconds || 0);
  return { totalSeconds, useCountdown: themeUsesCountdown(td) };
};

const parseFiniteNumber = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const getHitScores = (td) => {
  // Requested rule: good hit penalizes score, bad hit rewards score.
  const goodPenalty = Math.abs(
    parseFiniteNumber(td?.Negative_points, Math.abs(DEFAULT_HIT_SCORE_GOOD))
  );
  const badReward = Math.abs(parseFiniteNumber(td?.points, DEFAULT_HIT_SCORE_BAD));

  return {
    hitScoreGood: -goodPenalty,
    hitScoreBad: badReward,
  };
};

const formatClock = (totalSecs) => {
  const safe = Math.max(0, Math.floor(totalSecs));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const readPersistedTimer = () => {
  try {
    const raw = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedTimer = (payload) => {
  try {
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors (private mode/quota).
  }
};

const readPersistedScore = () => {
  try {
    const raw = localStorage.getItem(SCORE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedScore = (payload) => {
  try {
    localStorage.setItem(SCORE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors (private mode/quota).
  }
};

const readLastReportSignature = () => {
  try {
    return sessionStorage.getItem(REPORT_SENT_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const writeLastReportSignature = (signature) => {
  try {
    if (!signature) {
      sessionStorage.removeItem(REPORT_SENT_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(REPORT_SENT_STORAGE_KEY, signature);
  } catch {
    // Ignore storage errors.
  }
};

const Game = () => {
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);
  const { status, user, error } = useSelector((state) => state.auth);
  
  const [holes, setHoles] = useState(() =>
    makeRandomPositions(
      Array.from({ length: HOLE_COUNT }, (_, i) => buildHole(i)),
      getHoleBounds(window.innerWidth <= 768)
    )
  );
  const [timeLeft, setTimeLeft] = useState(() => ROUND_SECONDS);

  const timerSettings = getTimerSettings(themeData);
  const { totalSeconds, useCountdown } = timerSettings;
  const { hitScoreGood, hitScoreBad } = getHitScores(themeData);
  const timerConfigKey = `${themeData?.themename || "default"}|${themeData?.timer || "05:00"}|${useCountdown ? 1 : 0}`;
  const hasThemeLoaded = Boolean(themeData);
  const isRunning = hasThemeLoaded && (useCountdown ? timeLeft > 0 : true);
  const elapsedSeconds = useCountdown
    ? Math.max(0, totalSeconds - timeLeft)
    : Math.max(0, timeLeft);
  const minuteMilestone = Math.floor(elapsedSeconds / 60);
  const currentPopIntervalMs = Math.max(
    MIN_POP_INTERVAL_MS,
    POP_INTERVAL_MS - minuteMilestone * POP_SPEEDUP_PER_MIN_MS
  );
  const currentMoveIntervalMs = Math.max(
    MIN_MOVE_INTERVAL_MS,
    HOLE_MOVE_INTERVAL_MS - minuteMilestone * MOVE_SPEEDUP_PER_MIN_MS
  );
  const currentMoveTransitionMs = Math.max(
    MIN_MOVE_TRANSITION_MS,
    Math.floor(currentMoveIntervalMs * 0.72)
  );
  const activeHoleCount =
    elapsedSeconds >= FINAL_PHASE_START_SECONDS
      ? FINAL_PHASE_ACTIVE_HOLES
      : elapsedSeconds >= SECOND_PHASE_START_SECONDS
        ? SECOND_PHASE_ACTIVE_HOLES
        : INITIAL_ACTIVE_HOLES;
  const [score, setScore] = useState(0);
  const [goodHits, setGoodHits] = useState(0);
  const [badHits, setBadHits] = useState(0);
  const [maxSmashStreak, setMaxSmashStreak] = useState(0);
  const [hitPointPopups, setHitPointPopups] = useState([]);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const totalHits = goodHits + badHits;
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [hammer, setHammer] = useState(() => ({
    ...getDefaultHammerPosition(window.innerWidth <= 768),
    smash: false,
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
  }));
  const holesRef = useRef(holes);
  holesRef.current = holes;
  const whackContextRef = useRef({
    isRunning,
    totalHits,
    hitScoreGood,
    hitScoreBad,
    isMobile,
  });
  whackContextRef.current = { isRunning, totalHits, hitScoreGood, hitScoreBad, isMobile };
  const hammerDraggingRef = useRef(false);
  const hammerDragOffsetRef = useRef({ x: 0, y: 0 });
  const hammerHitTimerRef = useRef(null);
  const hitAudioRef = useRef(null);
  const hitAudioPrimedRef = useRef(false);
  /** Bumps when a real hit plays so in-flight silent-prime `play().then` must not pause the element. */
  const hitAudioPrimeCleanupGenRef = useRef(0);
  const hitBeepCtxRef = useRef(null);
  const hitPointsTimersRef = useRef({});
  const spawnWaveStaggerPadRef = useRef(0);
  const gameOverReportedRef = useRef(false);
  const badChainRef = useRef(0);
  /** Peak consecutive bad-target hits across the entire game (persisted/restored separately from the live chain). */
  const maxConsecutiveBadRef = useRef(0);
  const [hammerSpriteSrc, setHammerSpriteSrc] = useState(hammerStill);

  const impactSoundSrc = useMemo(
    () => resolveImpactSoundUrl(themeData),
    [themeData]
  );

  useEffect(() => {
    hitAudioPrimeCleanupGenRef.current += 1;
    hitAudioPrimedRef.current = false;
    if (hitAudioRef.current) {
      try {
        hitAudioRef.current.pause();
        hitAudioRef.current.src = "";
      } catch {
        // ignore
      }
      hitAudioRef.current = null;
    }
  }, [impactSoundSrc]);

  useEffect(() => {
    if (window.innerWidth > 768) return undefined;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overscrollBehavior = prevBodyOverscroll;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [status, user]);

  useEffect(() => {
    if (!themeData) return;

    const persisted = readPersistedTimer();
    const now = Date.now();
    const elapsedSinceSave = Math.max(
      0,
      Math.floor((now - Number(persisted?.savedAt || 0)) / 1000)
    );

    const hasSameConfig =
      persisted?.configKey === timerConfigKey &&
      typeof persisted?.timeLeft === "number";
    const isRecentSave = now - Number(persisted?.savedAt || 0) <= MAX_TIMER_RESUME_AGE_MS;

    if (hasSameConfig && isRecentSave) {
      const restored = persisted.useCountdown
        ? Math.max(0, persisted.timeLeft - elapsedSinceSave)
        : Math.max(0, persisted.timeLeft + elapsedSinceSave);
      setTimeLeft(restored);
      return;
    }

    setTimeLeft(useCountdown ? totalSeconds : 0);
  }, [themeData?.themename, themeData?.timer, themeData?.use_timeout, timerConfigKey, totalSeconds, useCountdown]);

  useEffect(() => {
    if (!themeData) return;
    writePersistedTimer({
      configKey: timerConfigKey,
      useCountdown,
      timeLeft,
      savedAt: Date.now(),
    });
  }, [themeData?.themename, timerConfigKey, timeLeft, useCountdown]);

  useEffect(() => {
    if (!themeData) return;

    const persisted = readPersistedScore();
    const now = Date.now();
    const hasSameConfig = persisted?.configKey === timerConfigKey;
    const isRecentSave = now - Number(persisted?.savedAt || 0) <= MAX_TIMER_RESUME_AGE_MS;
    const hasValidCounters =
      Number.isFinite(Number(persisted?.score)) &&
      Number.isFinite(Number(persisted?.goodHits)) &&
      Number.isFinite(Number(persisted?.badHits));

    if (hasSameConfig && isRecentSave && hasValidCounters) {
      setScore(Number(persisted.score));
      setGoodHits(Number(persisted.goodHits));
      setBadHits(Number(persisted.badHits));
      const restoredPeak = Number.isFinite(Number(persisted?.maxSmashStreak))
        ? Math.max(0, Math.floor(Number(persisted.maxSmashStreak)))
        : 0;
      maxConsecutiveBadRef.current = restoredPeak;
      setMaxSmashStreak(restoredPeak);
      badChainRef.current = 0;
      return;
    }

    setScore(0);
    setGoodHits(0);
    setBadHits(0);
    maxConsecutiveBadRef.current = 0;
    setMaxSmashStreak(0);
    badChainRef.current = 0;
  }, [themeData?.themename, themeData?.timer, themeData?.use_timeout, timerConfigKey]);

  useEffect(() => {
    if (!themeData) return;
    writePersistedScore({
      configKey: timerConfigKey,
      score,
      goodHits,
      badHits,
      maxSmashStreak,
      count: totalHits,
      savedAt: Date.now(),
    });
  }, [themeData?.themename, timerConfigKey, score, goodHits, badHits, maxSmashStreak, totalHits]);

    useEffect(() => {
      dispatch(fetchThemeData({ themeId: null }));
    }, [dispatch]);
  
    useEffect(() => {
      dispatch(setBackButtonUrl("/rules"));
    }, [status, user]);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
      console.log('Fetching theme data',fetchThemeData);
    }
  }, [dispatch, themeStatus]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (hammerDraggingRef.current || hammer.smash) return;
    setHammer((prev) => ({
      ...prev,
      ...getDefaultHammerPosition(true),
    }));
  }, [isMobile]);

  const [goodSprites, setGoodSprites] = useState(LOCAL_GOOD_SPRITES);
  const [badSprites, setBadSprites] = useState(LOCAL_BAD_SPRITES);
  const lastUsedHolesRef = useRef([]);
  const hideTimersRef = useRef({});
  const hitLockRef = useRef({});
  const hitCooldownUntilRef = useRef({});
  const movementLockUntilRef = useRef(0);
  const moveTransitionTimerRef = useRef(null);
  const [isBoardMoving, setIsBoardMoving] = useState(false);
  const isBoardMovingRef = useRef(false);

  useEffect(() => {
    const loadSprites = async () => {
      const themeGood = parseArray(themeData?.good);
      const themeBad = parseArray(themeData?.bad);

      const mapResolved = async (list) => {
        const out = [];
        for (const sprite of list) {
          const url = await resolveThemeSpriteUrl(sprite);
          if (url) out.push(url);
        }
        return out;
      };

      if (themeGood.length > 0) {
        const validGoodSprites = await mapResolved(themeGood);
        if (validGoodSprites.length > 0) setGoodSprites(validGoodSprites);
      }

      if (themeBad.length > 0) {
        const validBadSprites = await mapResolved(themeBad);
        if (validBadSprites.length > 0) setBadSprites(validBadSprites);
      }
    };

    if (themeData) {
      void loadSprites();
    }
  }, [themeData]);

  const [boardBg, setBoardBg] = useState('');

  useEffect(() => {
    const loadBackground = async () => {
      const deskRaw = themeData?.game_background_desk;
      const mobRaw = themeData?.game_background_mob;
      const deskS3 = buildThemeAssetUrl(deskRaw);
      const mobS3 = buildThemeAssetUrl(mobRaw);
      const deskLocal = /^https?:\/\//i.test(String(deskRaw ?? "").trim())
        ? ""
        : buildLocalThemeSpriteUrl(deskRaw);
      const mobLocal = /^https?:\/\//i.test(String(mobRaw ?? "").trim())
        ? ""
        : buildLocalThemeSpriteUrl(mobRaw);

      const pickPair = async (first, firstLocal, second, secondLocal) => {
        const primary = await pickReachableImageUrl(first, firstLocal);
        if (primary) return primary;
        return await pickReachableImageUrl(second, secondLocal);
      };

      const chosen = isMobile
        ? await pickPair(mobS3, mobLocal, deskS3, deskLocal)
        : await pickPair(deskS3, deskLocal, mobS3, mobLocal);

      const preferredFallback = isMobile ? mobS3 || deskS3 : deskS3 || mobS3;
      setBoardBg(chosen || preferredFallback);
    };

    if (themeData) {
      void loadBackground();
    }
  }, [isMobile, themeData]);

  useEffect(() => {
    if (!hasThemeLoaded || !isRunning) return undefined;

    const { useCountdown: cd } = getTimerSettings(themeData);
    const tick = cd
      ? () => setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0))
      : () => setTimeLeft((prev) => prev + 1);

    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [hasThemeLoaded, isRunning, themeData?.use_timeout, themeData?.timer]);

  useEffect(() => {
    if (!isRunning) return undefined;
    return () => {
      if (moveTransitionTimerRef.current) {
        window.clearTimeout(moveTransitionTimerRef.current);
        moveTransitionTimerRef.current = null;
      }
      isBoardMovingRef.current = false;
      setIsBoardMoving(false);
    };
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) return undefined;

    const hasGoodSprites = goodSprites.length > 0;
    const hasBadSprites = badSprites.length > 0;
    if (!hasGoodSprites && !hasBadSprites) return undefined;

    const queueHoleDown = (holeId) => {
      if (hideTimersRef.current[holeId]) {
        window.clearTimeout(hideTimersRef.current[holeId]);
      }
      movementLockUntilRef.current = Math.max(
        Number(movementLockUntilRef.current || 0),
        Date.now() + GOING_DOWN_DURATION_MS
      );
      setHoles((prev) =>
        prev.map((h) =>
          h.id === holeId && h.popType !== "none"
            ? { ...h, goingDown: true, visibleUntil: 0 }
            : h
        )
      );
      hideTimersRef.current[holeId] = window.setTimeout(() => {
        setHoles((prev) =>
          prev.map((h) =>
            h.id === holeId
              ? { ...h, popType: "none", goingDown: false, visibleUntil: 0 }
              : h
          )
        );
      }, GOING_DOWN_DURATION_MS);
    };

    const scheduleMoleHide = (holeId, visibleMs) => {
      if (hideTimersRef.current[holeId]) {
        window.clearTimeout(hideTimersRef.current[holeId]);
      }
      hideTimersRef.current[holeId] = window.setTimeout(() => {
        queueHoleDown(holeId);
      }, visibleMs);
    };

    const spawnStaggerTimers = [];

    const spawnWave = () => {
      if (isBoardMovingRef.current) return;
      if (Date.now() < Number(movementLockUntilRef.current || 0)) return;

      spawnWaveStaggerPadRef.current = 0;

      const prev = holesRef.current;
      const now = Date.now();
      const inactiveIndices = prev
        .map((h, i) => {
          if (h.id >= activeHoleCount) return -1;
          const cooldownUntil = Number(hitCooldownUntilRef.current[h.id] || 0);
          const isCoolingDown = cooldownUntil > now;
          return h.popType === "none" && !h.goingDown && !isCoolingDown ? i : -1;
        })
        .filter((i) => i !== -1);

      const availableIndices = inactiveIndices.filter(
        (i) => !lastUsedHolesRef.current.includes(i)
      );
      const pickFrom = availableIndices.length > 0 ? availableIndices : inactiveIndices;
      if (pickFrom.length === 0) return;

      const isFiveHolePhase = activeHoleCount >= FINAL_PHASE_ACTIVE_HOLES;
      const avail = pickFrom.length;
      const minWave = isFiveHolePhase
        ? Math.min(FINAL_PHASE_MIN_MOLES, avail)
        : Math.min(MIN_ACTIVE_MOLES, avail);
      const maxWave = isFiveHolePhase
        ? Math.min(FINAL_PHASE_MAX_MOLES, avail)
        : Math.min(MAX_ACTIVE_MOLES, avail);
      const span = Math.max(0, maxWave - minWave);
      const randomCount = minWave + (span <= 0 ? 0 : randomInt(span + 1));
      const numToActivate = Math.min(randomCount, avail);

      const shuffled = [...pickFrom].sort(() => Math.random() - 0.5);
      const chosenIndices = shuffled.slice(0, numToActivate);
      lastUsedHolesRef.current = chosenIndices;

      let cumulativeDelay = 0;
      chosenIndices.forEach((idx, order) => {
        if (order > 0) {
          cumulativeDelay +=
            MIN_SPAWN_STAGGER_MS +
            randomInt(MAX_SPAWN_STAGGER_MS - MIN_SPAWN_STAGGER_MS + 1);
        }
        spawnWaveStaggerPadRef.current = cumulativeDelay;

        const tid = window.setTimeout(() => {
          setHoles((p) => {
            if (isBoardMovingRef.current) return p;
            const hole = p[idx];
            if (!hole || hole.id >= activeHoleCount) return p;
            if (hole.popType !== "none" || hole.goingDown) return p;
            const cu = Number(hitCooldownUntilRef.current[hole.id] || 0);
            if (cu > Date.now()) return p;

            let popType = Math.random() < 0.35 ? "good" : "bad";
            if (popType === "good" && !hasGoodSprites) popType = "bad";
            if (popType === "bad" && !hasBadSprites) popType = "good";
            const spriteIndex =
              popType === "good"
                ? randomInt(goodSprites.length)
                : randomInt(badSprites.length);

            const next = [...p];
            next[idx] = {
              ...hole,
              popType,
              spriteIndex,
              hitPulse: false,
              isSqueezing: false,
              goingDown: false,
            };

            const visibleMs =
              MIN_VISIBLE_MS + randomInt(MAX_VISIBLE_MS - MIN_VISIBLE_MS + 1);
            const visibleUntil = Date.now() + visibleMs;
            movementLockUntilRef.current = Math.max(
              Number(movementLockUntilRef.current || 0),
              visibleUntil + GOING_DOWN_DURATION_MS
            );
            scheduleMoleHide(next[idx].id, visibleMs);
            next[idx] = { ...next[idx], visibleUntil };

            return next;
          });
        }, cumulativeDelay);
        spawnStaggerTimers.push(tid);
      });
    };

    const startHoleMove = () => {
      if (isBoardMovingRef.current) return;
      const hasVisibleMoles = holesRef.current.some(
        (h) => h.id < activeHoleCount && (h.popType !== "none" || h.goingDown)
      );
      if (hasVisibleMoles) return;

      isBoardMovingRef.current = true;
      movementLockUntilRef.current = Math.max(
        Number(movementLockUntilRef.current || 0),
        Date.now() + currentMoveTransitionMs + 60
      );
      setIsBoardMoving(true);
      setHoles((prev) => makeRandomPositions(prev, getHoleBounds(isMobile)));

      if (moveTransitionTimerRef.current) {
        window.clearTimeout(moveTransitionTimerRef.current);
      }
      moveTransitionTimerRef.current = window.setTimeout(() => {
        isBoardMovingRef.current = false;
        setIsBoardMoving(false);
      }, currentMoveTransitionMs + 40);
    };

    const scheduleCycle = (delayMs) =>
      window.setTimeout(() => {
        if (isBoardMovingRef.current || Date.now() < Number(movementLockUntilRef.current || 0)) {
          cycleTimer = scheduleCycle(POP_RETRY_WHILE_MOVING_MS);
          return;
        }

        startHoleMove();
        cycleTimer = window.setTimeout(() => {
          spawnWave();
          const staggerPad = spawnWaveStaggerPadRef.current;
          spawnWaveStaggerPadRef.current = 0;
          cycleTimer = scheduleCycle(currentPopIntervalMs + staggerPad);
        }, currentMoveTransitionMs + 80);
      }, delayMs);

    let cycleTimer = scheduleCycle(120);
    const staleMoleWatchdog = window.setInterval(() => {
      const now = Date.now();
      const staleIds = [];
      setHoles((prev) => {
        const next = prev.map((h) => {
          const isStale =
            h.popType !== "none" &&
            !h.goingDown &&
            h.visibleUntil > 0 &&
            now > h.visibleUntil + 120;
          if (!isStale) return h;
          staleIds.push(h.id);
          return h;
        });
        return staleIds.length ? next : prev;
      });
      staleIds.forEach((id) => queueHoleDown(id));
    }, 220);

    return () => {
      spawnStaggerTimers.forEach((tid) => window.clearTimeout(tid));
      window.clearTimeout(cycleTimer);
      window.clearInterval(staleMoleWatchdog);
      Object.values(hideTimersRef.current).forEach((t) => window.clearTimeout(t));
      hideTimersRef.current = {};
      hitLockRef.current = {};
      setHoles((prev) =>
        prev.map((h) =>
          h.popType === "none" && !h.goingDown
            ? h
            : { ...h, popType: "none", goingDown: false, visibleUntil: 0 }
        )
      );
    };
  }, [isRunning, goodSprites, badSprites, currentPopIntervalMs, activeHoleCount]);

  useEffect(() => {
    if (!user) return;
    // Multiplay: allow replay after fresh login; block only same visit (sessionStorage).
    if (isMultiplaySession(user)) {
      if (readMultiplayRoundDone()) {
        setAlreadyPlayed(true);
      }
      return;
    }
    // Single play (isMultiplaySession null/undefined/false): one round via fetchReport.
    if (!themeData) return;
    const userId = user?.userId || user?.id;
    const sessionId = user?.sessionId;
    const organizationId = user?.organizationId;
    if (!userId || !sessionId || !organizationId || !adminToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const reportData = await fetchReport({
          userId,
          sessionId,
          organizationId,
          token: adminToken,
        });
        if (cancelled) return;
        if (reportData?.report) {
          setAlreadyPlayed(true);
        }
      } catch {
        // Keep gameplay available if report check fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, themeData, user]);

  useEffect(() => {
    const reachedHitTarget = totalHits >= TARGET_TOTAL_HITS;
    const countdownFinished = useCountdown && hasThemeLoaded && !isRunning;
    const isGameFinished = reachedHitTarget || countdownFinished;
    if (!isGameFinished) return;

    if (isMultiplaySession(user) && !alreadyPlayed) {
      writeMultiplayRoundDone();
      setAlreadyPlayed(true);
    }
console.log("alreadyPlayed-------", isMultiplaySession(user),alreadyPlayed);
    if (!alreadyPlayed) {
      navigate("/leaderboard");
    }
  }, [alreadyPlayed, hasThemeLoaded, isRunning, navigate, totalHits, useCountdown, user]);

  useEffect(() => {
    const sessionId = String(user?.sessionId ?? "").trim().toLowerCase();
    if (!sessionId.startsWith("demo")) return;
    const redirectTimer = window.setTimeout(() => {
      window.location.assign("https://staging.extramileplay.com/plans");
    }, 20000);
    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [user?.sessionId]);

  useEffect(
    () => () => {
      if (hammerHitTimerRef.current != null) {
        window.clearTimeout(hammerHitTimerRef.current);
        hammerHitTimerRef.current = null;
      }
    },
    []
  );

  useEffect(
    () => () => {
      if (hitAudioRef.current) {
        hitAudioRef.current.pause();
        hitAudioRef.current = null;
      }
      hitAudioPrimedRef.current = false;
      Object.values(hitPointsTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      hitPointsTimersRef.current = {};
    },
    []
  );

  const triggerHammer = () => {
    if (hammerHitTimerRef.current != null) {
      window.clearTimeout(hammerHitTimerRef.current);
      hammerHitTimerRef.current = null;
    }
    setHammer((prev) => ({ ...prev, smash: true }));
    setHammerSpriteSrc(HAMMER_HIT_FRAMES[0]);
    let idx = 0;
    const advance = () => {
      idx += 1;
      if (idx < HAMMER_HIT_FRAMES.length) {
        setHammerSpriteSrc(HAMMER_HIT_FRAMES[idx]);
        hammerHitTimerRef.current = window.setTimeout(advance, HAMMER_FRAME_MS);
      } else {
        setHammerSpriteSrc(hammerStill);
        setHammer((prev) => ({ ...prev, smash: false }));
        hammerHitTimerRef.current = null;
      }
    };
    hammerHitTimerRef.current = window.setTimeout(advance, HAMMER_FRAME_MS);
  };

  /** Visual swing only — impact sound stays in applyWhack for real hits. */
  const swingHammerAt = (clientX, clientY) => {
    if (
      typeof clientX !== "number" ||
      typeof clientY !== "number" ||
      !Number.isFinite(clientX) ||
      !Number.isFinite(clientY)
    ) {
      return;
    }
    const hitOffset = isMobile ? MOBILE_HAMMER_HIT_OFFSET : HAMMER_CURSOR_OFFSET;
    setHammer((prev) => ({
      ...prev,
      x: clientX - hitOffset.x,
      y: clientY - hitOffset.y,
    }));
    triggerHammer();
  };

  const getHitAudio = () => {
    if (!impactSoundSrc) return null;
    if (!hitAudioRef.current) {
      const audio = new Audio();
      audio.preload = "auto";
      if ("playsInline" in audio) audio.playsInline = true;
      audio.volume = 1;
      audio.src = impactSoundSrc;
      audio.load();
      hitAudioRef.current = audio;
    }
    return hitAudioRef.current;
  };

  const playOscillatorHitFallback = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!hitBeepCtxRef.current) hitBeepCtxRef.current = new Ctx();
      const ctx = hitBeepCtxRef.current;
      const beep = () => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.value = 440;
          gain.gain.value = 0.5;
          osc.connect(gain);
          gain.connect(ctx.destination);
          const now = ctx.currentTime;
          osc.start(now);
          osc.stop(now + 0.15);
        } catch {
          /* ignore */
        }
      };
      if (ctx.state === "suspended") {
        void ctx.resume().then(beep);
      } else {
        beep();
      }
    } catch {
      /* ignore */
    }
  };

  /** Call from pointer/tap handlers so mobile browsers allow playback (esp. iOS Safari). */
  const unlockHitAudio = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !hitBeepCtxRef.current) hitBeepCtxRef.current = new Ctx();
      if (hitBeepCtxRef.current?.state === "suspended") {
        void hitBeepCtxRef.current.resume();
      }

      if (!impactSoundSrc) return;
      const audio = getHitAudio();
      if (!audio) return;
      if (hitAudioPrimedRef.current) return;
      hitAudioPrimedRef.current = true;

      // Prime by playing silently then pausing — unlocks iOS/Safari autoplay restriction
      const prevVol = audio.volume;
      const primeGen = hitAudioPrimeCleanupGenRef.current;
      audio.volume = 0.001;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            if (hitAudioPrimeCleanupGenRef.current !== primeGen) return;
            audio.pause();
            audio.currentTime = 0;
            audio.volume = prevVol;
          })
          .catch(() => {
            hitAudioPrimedRef.current = false;
            audio.volume = prevVol;
          });
      }
    } catch {
      hitAudioPrimedRef.current = false;
    }
  };

  const playHitSound = () => {
    if (!impactSoundSrc) {
      playOscillatorHitFallback();
      return;
    }

    const playSrc = (src, allowLocalRetry) => {
      const audio = new Audio(src);
      audio.volume = 1;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          if (allowLocalRetry && src !== localImpactSound) playSrc(localImpactSound, false);
          else playOscillatorHitFallback();
        });
      }
    };

    playSrc(impactSoundSrc, impactSoundSrc !== localImpactSound);
  };

  /** Find hole under hammer head/center probes and keep hit zone near top-center area. */
  const getHoleIdUnderPoint = (clientX, clientY) => {
    const probes = [
      { x: clientX + HAMMER_HIT_POINT_OFFSET.x, y: clientY + HAMMER_HIT_POINT_OFFSET.y },
      { x: clientX + HAMMER_HIT_CENTER_OFFSET.x, y: clientY + HAMMER_HIT_CENTER_OFFSET.y },
    ];

    const hammerEl = document.querySelector(".game-hammer");
    const prevPe = hammerEl?.style.pointerEvents;
    if (hammerEl) hammerEl.style.pointerEvents = "none";

    const findByPoint = (hitX, hitY) => {
      const el = document.elementFromPoint(hitX, hitY);
      const btn = el?.closest?.("[data-game-hole]");
      if (!btn) return null;
      const rect = btn.getBoundingClientRect();
      const hitBottomY = rect.top + rect.height * HOLE_TOP_HIT_RATIO;
      if (hitY > hitBottomY) return null;
      const raw = btn.getAttribute("data-game-hole");
      const id = raw == null ? NaN : Number(raw);
      return Number.isFinite(id) ? id : null;
    };

    let matchedId = null;
    for (let i = 0; i < probes.length; i += 1) {
      const byPoint = findByPoint(probes[i].x, probes[i].y);
      if (byPoint != null) {
        matchedId = byPoint;
        break;
      }
    }

    if (hammerEl) hammerEl.style.pointerEvents = prevPe || "";
    if (matchedId != null) return matchedId;

    // Fallback: geometric check for active holes (helps when sprite layering masks elementFromPoint).
    for (let i = 0; i < holesRef.current.length; i += 1) {
      const hole = holesRef.current[i];
      if (!hole || hole.popType === "none" || hole.goingDown) continue;
      const btn = document.querySelector(`[data-game-hole="${hole.id}"]`);
      if (!btn) continue;
      const rect = btn.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const topLimit = rect.top;
      const hitBottomY = rect.top + rect.height * HOLE_TOP_HIT_RATIO;
      const maxDx = rect.width * HOLE_HIT_MAX_DX_RATIO;
      for (let p = 0; p < probes.length; p += 1) {
        const probe = probes[p];
        if (
          Math.abs(probe.x - centerX) <= maxDx &&
          probe.y >= topLimit &&
          probe.y <= hitBottomY
        ) {
          return hole.id;
        }
      }
    }

    return null;
  };

  const applyWhack = (holeId, clientX, clientY) => {
    const {
      isRunning: run,
      totalHits: hits,
      hitScoreGood: goodPts,
      hitScoreBad: badPts,
    } = whackContextRef.current;
    if (!run || hits >= TARGET_TOTAL_HITS) {
      return;
    }

    const targetHole = holesRef.current.find((h) => h.id === holeId);
    if (!targetHole || targetHole.popType === "none" || targetHole.goingDown) {
      return;
    }
    if (hitLockRef.current[holeId]) return;

    let animX = clientX;
    let animY = clientY;
    if (
      typeof animX !== "number" ||
      typeof animY !== "number" ||
      !Number.isFinite(animX) ||
      !Number.isFinite(animY)
    ) {
      const holeEl = document.querySelector(`[data-game-hole="${holeId}"]`);
      const rect = holeEl?.getBoundingClientRect?.();
      if (rect) {
        animX = rect.left + rect.width * 0.5;
        animY = rect.top + rect.height * 0.35;
      }
    }
    if (
      typeof animX === "number" &&
      typeof animY === "number" &&
      Number.isFinite(animX) &&
      Number.isFinite(animY)
    ) {
      const hitOffset = isMobile ? MOBILE_HAMMER_HIT_OFFSET : HAMMER_CURSOR_OFFSET;
      setHammer((prev) => ({
        ...prev,
        x: animX - hitOffset.x,
        y: animY - hitOffset.y,
      }));
      triggerHammer();
    }

    if (hideTimersRef.current[holeId]) {
      window.clearTimeout(hideTimersRef.current[holeId]);
      delete hideTimersRef.current[holeId];
    }

    const { popType } = targetHole;
    if (popType !== "good" && popType !== "bad") return;
    hitLockRef.current[holeId] = true;
    movementLockUntilRef.current = Math.max(
      Number(movementLockUntilRef.current || 0),
      Date.now() + HIT_HIDE_DELAY_MS
    );
    const pointsDelta = popType === "good" ? goodPts : badPts;

    const holeEl = document.querySelector(`[data-game-hole="${holeId}"]`);
    if (holeEl) {
      const rect = holeEl.getBoundingClientRect();
      const popupId = `${holeId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setHitPointPopups((prev) => [
        ...prev,
        {
          id: popupId,
          x: rect.left + rect.width * 0.52,
          y: rect.top + rect.height * 0.22,
          text: pointsDelta > 0 ? `+${pointsDelta}` : String(pointsDelta),
          kind: pointsDelta >= 0 ? "good" : "bad",
        },
      ]);
      hitPointsTimersRef.current[popupId] = window.setTimeout(() => {
        setHitPointPopups((prev) => prev.filter((item) => item.id !== popupId));
        delete hitPointsTimersRef.current[popupId];
      }, HIT_POINTS_FADE_MS);
    }

    playHitSound();
    if (popType === "good") {
      badChainRef.current = 0;
      setScore((s) => s + goodPts);
      setGoodHits((v) => v + 1);
    } else if (popType === "bad") {
      badChainRef.current += 1;
      maxConsecutiveBadRef.current = Math.max(
        maxConsecutiveBadRef.current,
        badChainRef.current
      );
      setMaxSmashStreak(maxConsecutiveBadRef.current);
      setScore((s) => s + badPts);
      setBadHits((v) => v + 1);
    }

    setHoles((prev) =>
      prev.map((hole) =>
        hole.id === holeId
          ? {
              ...hole,
              hitPulse: true,
              isSqueezing: true,
              goingDown: false,
              visibleUntil: 0,
            }
          : hole
      )
    );
    hitCooldownUntilRef.current[holeId] = Date.now() + HIT_RESPAWN_COOLDOWN_MS;
    hideTimersRef.current[holeId] = window.setTimeout(() => {
      setHoles((prev) =>
        prev.map((h) =>
          h.id === holeId
            ? {
                ...h,
                popType: "none",
                hitPulse: false,
                isSqueezing: false,
                goingDown: false,
                visibleUntil: 0,
              }
            : h
        )
      );
      delete hideTimersRef.current[holeId];
      delete hitLockRef.current[holeId];
    }, HIT_HIDE_DELAY_MS);
  };

  const finalizeHammerSwingRelease = (clientX, clientY, wasDragging) => {
    if (!wasDragging) return;
    if (
      typeof clientX !== "number" ||
      typeof clientY !== "number" ||
      !Number.isFinite(clientX) ||
      !Number.isFinite(clientY)
    ) {
      return;
    }
    const { isRunning: run, totalHits: hits } = whackContextRef.current;
    if (!run || hits >= TARGET_TOTAL_HITS) return;
    const holeId = getHoleIdUnderPoint(clientX, clientY);
    if (holeId != null) {
      applyWhack(holeId, clientX, clientY);
    } else if (hammerHitTimerRef.current == null) {
      // Avoid a second swing if a drag-move already triggered the hit animation this frame chain.
      swingHammerAt(clientX, clientY);
    }
  };

  const handleHammerPointerDown = (e) => {
    if (hammer.smash) return;
    e.preventDefault();
    e.stopPropagation();
    unlockHitAudio();
    const rect = e.currentTarget.getBoundingClientRect();
    hammerDragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    hammerDraggingRef.current = true;
    setHammer((prev) => ({
      ...prev,
      isDragging: true,
      dragOffset: { ...hammerDragOffsetRef.current },
      smash: false,
    }));
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore if capture unsupported
    }
  };

  /** Mobile comfort: allow starting hammer drag from lower screen control zone. */
  const handleMobileControlPointerDown = (e) => {
    if (!isMobile) return;
    if (hammer.smash) return;
    if (typeof e.clientY !== "number") return;
    if (e.clientY < window.innerHeight * MOBILE_CONTROL_ZONE_START) return;

    e.preventDefault();
    unlockHitAudio();
    hammerDragOffsetRef.current = { ...HAMMER_CURSOR_OFFSET };
    hammerDraggingRef.current = true;
    setHammer((prev) => ({
      ...prev,
      x: e.clientX - HAMMER_CURSOR_OFFSET.x,
      y: e.clientY - HAMMER_CURSOR_OFFSET.y,
      isDragging: true,
      dragOffset: { ...HAMMER_CURSOR_OFFSET },
      smash: false,
    }));

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore if capture unsupported
    }
  };

  const handleHammerPointerMove = (e) => {
    if (!hammerDraggingRef.current) return;
    const { x: ox, y: oy } = hammerDragOffsetRef.current;
    setHammer((prev) => ({
      ...prev,
      x: e.clientX - ox,
      y: e.clientY - oy,
    }));
    const holeId = getHoleIdUnderPoint(e.clientX, e.clientY);
    if (holeId != null) {
      applyWhack(holeId, e.clientX, e.clientY);
    }
  };

  const endHammerDrag = (e) => {
    if (!hammerDraggingRef.current) return;
    hammerDraggingRef.current = false;
    try {
      if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
    setHammer((prev) => ({ ...prev, isDragging: false }));
  };

  const handleHammerPointerUp = (e) => {
    const wasDragging = hammerDraggingRef.current;
    const cx = e.clientX;
    const cy = e.clientY;
    endHammerDrag(e);
    finalizeHammerSwingRelease(cx, cy, wasDragging);
  };

  const handleHammerPointerCancel = (e) => {
    endHammerDrag(e);
  };

  const handleHammerLostPointerCapture = () => {
    hammerDraggingRef.current = false;
    setHammer((prev) => ({ ...prev, isDragging: false }));
  };

  const handleMobileControlPointerMove = (e) => {
    if (!isMobile) {
      if (hammerDraggingRef.current || hammerHitTimerRef.current != null) return;
      const nextX = clamp(
        e.clientX - HAMMER_CURSOR_OFFSET.x,
        -24,
        Math.max(-24, window.innerWidth - 132)
      );
      const nextY = clamp(
        e.clientY - HAMMER_CURSOR_OFFSET.y,
        -16,
        Math.max(-16, window.innerHeight - 138)
      );
      setHammer((prev) => ({
        ...prev,
        x: nextX,
        y: nextY,
      }));
      return;
    }

    if (!hammerDraggingRef.current) return;
    const { x: ox, y: oy } = hammerDragOffsetRef.current;
    setHammer((prev) => ({
      ...prev,
      x: e.clientX - ox,
      y: e.clientY - oy,
    }));
    const holeId = getHoleIdUnderPoint(e.clientX, e.clientY);
    if (holeId != null) {
      applyWhack(holeId, e.clientX, e.clientY);
    }
  };

  const handleMobileControlPointerUp = (e) => {
    if (!isMobile) return;
    const wasDragging = hammerDraggingRef.current;
    const cx = e.clientX;
    const cy = e.clientY;
    endHammerDrag(e);
    finalizeHammerSwingRelease(cx, cy, wasDragging);
  };

  useEffect(() => {
    // Apply background image to body using boardBg state
    if (boardBg) {
      document.body.style.backgroundImage = cssBackgroundImageUrl(boardBg);
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundSize = 'cover';
    } else {
      // Clear background if no boardBg
      document.body.style.backgroundImage = 'none';
    }

    return () => {
      // Clean up background styles when component unmounts
      document.body.style.backgroundImage = 'none';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundSize = '';
    };
  }, [boardBg]);

  /** Tap/click hole to whack on both desktop and mobile. */
  const handleHoleTap = (holeId, event) => {
    unlockHitAudio();
    const cx = event?.clientX;
    const cy = event?.clientY;
    applyWhack(holeId, cx, cy);
  };

  const restartGame = () => {

    Object.values(hideTimersRef.current).forEach((t) => window.clearTimeout(t));
    hideTimersRef.current = {};
    hitLockRef.current = {};
    hitCooldownUntilRef.current = {};
    Object.values(hitPointsTimersRef.current).forEach((t) => window.clearTimeout(t));
    hitPointsTimersRef.current = {};
    lastUsedHolesRef.current = [];
    hammerDraggingRef.current = false;
    if (hammerHitTimerRef.current != null) {
      window.clearTimeout(hammerHitTimerRef.current);
      hammerHitTimerRef.current = null;
    }
    const { totalSeconds: cap, useCountdown: cd } = getTimerSettings(themeData);
    setTimeLeft(cd ? cap : 0);
    setScore(0);
    setGoodHits(0);
    setBadHits(0);
    badChainRef.current = 0;
    maxConsecutiveBadRef.current = 0;
    setMaxSmashStreak(0);
    setHitPointPopups([]);
    setHoles(
      makeRandomPositions(
        Array.from({ length: HOLE_COUNT }, (_, i) => buildHole(i)),
        getHoleBounds(window.innerWidth <= 768)
      )
    );
    setHammerSpriteSrc(hammerStill);
    setHammer({
      ...getDefaultHammerPosition(window.innerWidth <= 768),
      smash: false,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
    });
    gameOverReportedRef.current = false;
    writeLastReportSignature("");
  };

  useEffect(() => {
    if (isRunning) {
      gameOverReportedRef.current = false;
    }
  }, [isRunning]);

  useEffect(() => {
    const isGameOver = hasThemeLoaded && (!isRunning || totalHits >= TARGET_TOTAL_HITS);
    if (!isGameOver || gameOverReportedRef.current) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const sessionId = user?.sessionId;
    const organizationId = user?.organizationId;
    const userId = user?.userId || user?.id;
    const name = user?.name;
    const email = user?.email;
    const role = user?.role;
    if (!sessionId || !organizationId || !userId || !name || !role) return;

    // Single-play: report already exists (e.g. user returned from leaderboard) — skip duplicate submit.
    if (!isMultiplaySession(user) && alreadyPlayed) {
      gameOverReportedRef.current = true;
      return;
    }

    const reportSignature = JSON.stringify({
      sessionId,
      organizationId,
      userId,
      timerConfigKey,
      role,
      time: elapsedSeconds,
      reportId: user?.reportId || null,
    });
    if (readLastReportSignature() === reportSignature) {
      gameOverReportedRef.current = true;
      return;
    }

    gameOverReportedRef.current = true;
    writeLastReportSignature(reportSignature);
    void (async () => {
      const formattedElapsedTime = `00:${String(elapsedSeconds).padStart(2, "0")}`;
      const reportData = {
        sessionId,
        role,
        token: user?.token || null,
        name,
        userId,
        gameId: user?.gameId || "",
        organizationId,
        points: score,
        time: formattedElapsedTime,
        reportId: user?.reportId || null,
        ans: "",
      };
      console.log(reportData);
      try {
        await sendReport(reportData);
      } catch (reportErr) {
        console.warn("Report request failed:", reportErr);
      }

      if (!backendUrl || !email) return;
      const token = `${sessionId}&${organizationId}`;
      try {
        const submitRes = await fetch(`${backendUrl}/submitReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userid: userId,
            name,
            email,
            user_type: role || "ORG_USER",
            sessionId,
            organizationId,
            score,
            time: elapsedSeconds,
            good: goodHits,
            bad: badHits,
            smashStreak: maxConsecutiveBadRef.current,
            count: totalHits,
            status: "completed",
          }),
        });
        if (!submitRes.ok) {
          throw new Error(`submitReport failed with ${submitRes.status}`);
        }
      } catch (submitErr) {
        console.error("submitReport failed after game over:", submitErr);
      }
    })();
  }, [
    alreadyPlayed,
    badHits,
    elapsedSeconds,
    goodHits,
    hasThemeLoaded,
    isRunning,
    maxSmashStreak,
    score,
    timerConfigKey,
    totalHits,
    user?.email,
    user?.gameId,
    user?.id,
    user?.name,
    user?.organizationId,
    user?.reportId,
    user?.role,
    user?.sessionId,
    user?.token,
    user?.userId,
  ]);

  return (
    <div
      className="game-page"
      onPointerDown={handleMobileControlPointerDown}
      onPointerMove={handleMobileControlPointerMove}
      onPointerUp={handleMobileControlPointerUp}
      onPointerCancel={handleMobileControlPointerUp}
    >
      {themeData?.follow_text && (
        <div className="game-follow-title"   style={{
          backgroundImage: `linear-gradient(180deg, ${themeData?.button_color || "#7c3aed"}, ${themeData?.button_color || "#7c3aed"})`,
          color: themeData?.button_text_color
        }}>{themeData.follow_text}</div>
      )}

      <div className="game-hud"     style={{
              background: themeData?.button_background
                ? themeData.button_background
                : `linear-gradient(180deg, ${themeData?.button_color || "#7c3aed"}, ${themeData?.button_color || "#7c3aed"})`,
              color: themeData?.button_text_color ? themeData.button_text_color : "#fff",
            }}>
        <div className="game-stat" style={{ color: themeData?.button_text_color }}>
          <span style={{ color: themeData?.button_text_color }}>{useCountdown ? "Time left" : "Time"}</span>
          <strong>{formatClock(timeLeft)}</strong>
        </div>
        <div className="game-stat"><span style={{ color: themeData?.button_text_color }}>Score</span><strong style={{ color: themeData?.button_text_color }}>{score}</strong></div>
        <div className="game-stat"><span style={{ color: themeData?.button_text_color }}>Good</span><strong style={{ color: themeData?.button_text_color }}>{goodHits}</strong></div>
        <div className="game-stat"><span style={{ color: themeData?.button_text_color }}>Bad</span><strong style={{ color: themeData?.button_text_color }}>{badHits}</strong></div>
      </div>

      <div className="game-playground">
        {holes.slice(0, activeHoleCount).map((hole) => {
          const spritePool = hole.popType === "good" ? goodSprites : badSprites;
          const characterSrc = spritePool[hole.spriteIndex % spritePool.length];

          const isActive = hole.popType !== "none" && !hole.goingDown;
          const isGoingDown = hole.goingDown;
          const showCharacter = hole.popType !== "none" || hole.goingDown;
          const isSqueezing = hole.isSqueezing;
          const driftSrc = isActive ? driftOpen : driftHole;

          return (
            <button
              key={hole.id}
              type="button"
              data-game-hole={String(hole.id)}
              className={`game-hole ${isActive ? "is-active" : ""} ${isGoingDown ? "is-going-down" : ""} ${hole.hitPulse ? "is-hit" : ""} ${isSqueezing ? "is-squeezing" : ""}`}
              style={{
                left: `${hole.x}%`,
                top: `${hole.y}%`,
                transition: `left ${currentMoveTransitionMs}ms ease-in-out, top ${currentMoveTransitionMs}ms ease-in-out`,
                zIndex: isSqueezing ? 100000 : undefined,
              }}
              onPointerDown={(e) => {
                if (e.button !== 0 && e.pointerType === "mouse") return;
                e.stopPropagation();
                unlockHitAudio();
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleHoleTap(hole.id, e);
              }}
              aria-label={`Drift hole ${hole.id + 1}`}
            >
              {showCharacter && (
                <img
                  src={characterSrc}
                  alt={hole.popType === "good" ? "Good character" : "Bad character"}
                  className="game-character"
                  draggable="false"
                />
              )}
              <img
                key={`drift-${hole.id}-${driftSrc}`}
                src={driftSrc}
                alt="drift"
                className="game-drift"
                draggable="false"
              />
            </button>
          );
        })}
      </div>

      {hitPointPopups.map((popup) => (
        <span
          key={popup.id}
          className={`game-point game-point-${popup.kind}`}
          style={{
            left: popup.x,
            top: popup.y,
          }}
          aria-hidden
        >
          {popup.text}
        </span>
      ))}

      {alreadyPlayed && (
        <>
          <div className="game-overlay" />
          <div className="game-finish">
            <h2>Already Played</h2>
            <p>You have already played this game.</p>
            <button type="button" onClick={() => navigate("/leaderboard")}>
              OK
            </button>
          </div>
        </>
      )}

      {!alreadyPlayed && (
        <img
          src={hammerSpriteSrc}
          alt=""
          className={`game-hammer ${hammer.smash ? "smash" : ""} ${hammer.isDragging ? "is-dragging" : ""} ${isMobile && !hammer.smash ? "game-hammer--mobile-hidden" : ""}`}
          style={{
            left: hammer.x,
            top: hammer.y,
          }}
          draggable="false"
          aria-hidden
          onPointerDown={handleHammerPointerDown}
          onPointerMove={handleHammerPointerMove}
          onPointerUp={handleHammerPointerUp}
          onPointerCancel={handleHammerPointerCancel}
          onLostPointerCapture={handleHammerLostPointerCapture}
        />
      )}
    </div>
  );
};

export default Game;
